import { isPlatformBrowser } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  APP_ID,
  ErrorHandler,
  Injector,
  PLATFORM_ID,
  Provider,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';
import { NavigationStart, Router, provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { filter, take } from 'rxjs';
import { pwaAppRoutes } from './app-routes';
import { AuthService } from './auth/services/auth.service';
import { BillingScope } from './billing-consts';
import { CustomErrorHandlerService } from './error-handler';
import { GROWTHBOOK, gb } from './growthbook';
import { HANDLE_META } from './handle-meta';
import { PICFLOW_MAINTENANCE } from './maintenance';
import { NetworkInterceptor } from './network-interceptor';
import { PICFLOW_ANALYTICS } from './picflow-analytics';
import { PICFLOW_CHECKOUT, PicflowCheckout } from './picflow-checkout';
import { PICFLOW_CLERK } from './picflow-clerk';
import { PICFLOW_CONFIG } from './picflow-config';
import { PICFLOW_STORYBLOK } from './picflow-storyblok';
import { AppConfigService } from './providers/config.service';
import { routes } from './routes';
import { AnalyticsService } from './services/analytics.service';
import { BrowserService } from './services/browser.service';
import { GrowthBookExperimentService } from './services/growth-book-experiment.service';
import { LanguageService } from './services/language.service';
import { MaintenanceService } from './services/maintenance.service';
import { MetaService } from './services/meta.service';
import { NavigationHistoryService } from './services/navigation-history.service';
import { PageLoadDebugService } from './services/page-load-debug.service';
import { ScrollService } from './services/scroll.service';
import { SentryTracingService } from './services/sentry-tracing.service';
import { StoryblokService } from './services/storyblok.service';
import { ThemeService } from './services/theme.service';
import { tenantRoutes } from './tenant-routes';

function initializeApp(
  scroll: ScrollService,
  an: AnalyticsService,
  meta: MetaService,
  config: AppConfigService,
  maintenance: MaintenanceService,
  languageService: LanguageService,
  browserService: BrowserService,
  navigationHistoryService: NavigationHistoryService,
  themeService: ThemeService,
  router: Router
) {
  return () => {
    const routes = getRoutes(browserService);
    if (routes) router.resetConfig(routes);
  };
}

/**
 * Public galleries can't fetch the gallery until Clerk knows whether a user is signed in, so start Clerk on the
 * first navigation instead of waiting for the tenant guards. Waits for NavigationStart because Clerk's locale
 * comes from the path. Custom domains are skipped: their auth depends on a feature flag that isn't loaded yet.
 */
function startAuthOnTenantSubdomain() {
  const browserService = inject(BrowserService);
  const injector = inject(Injector);
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  if (!browserService.getTenantSubDomain()) return;
  inject(Router)
    .events.pipe(
      filter(event => event instanceof NavigationStart),
      take(1)
    )
    .subscribe(() => injector.get(AuthService).init());
}

function getRoutes(browserService: BrowserService) {
  const isTenantDomain = browserService.isTenantDomain();
  const isPwaApp = browserService.isPwaApp();

  if (isTenantDomain) return tenantRoutes;
  if (isPwaApp) return pwaAppRoutes;
}

export const appConfig = {
  providers: [
    provideHttpClient(withXhr(), withInterceptorsFromDi()), // not using withFetch because it breaks the upload progress events.
    provideClientHydration(withIncrementalHydration(), withEventReplay()),
    CookieService,
    { provide: APP_ID, useValue: 'picflow' },
    <Provider>{ provide: HTTP_INTERCEPTORS, useClass: NetworkInterceptor, multi: true },
    {
      provide: GROWTHBOOK,
      useFactory: (service: GrowthBookExperimentService) => gb(service.onExperimentViewed),
      deps: [GrowthBookExperimentService],
    },
    {
      provide: PICFLOW_MAINTENANCE,
      useFactory: (maintenance: MaintenanceService) => maintenance,
      deps: [MaintenanceService],
    },
    {
      provide: PICFLOW_CHECKOUT,
      useFactory: (injector: Injector) => {
        return <PicflowCheckout>{
          open: async (scope: BillingScope) => {
            const { CheckoutService } = await import('./services/checkout.service');
            injector.get(CheckoutService).open(scope);
          },
        };
      },
      deps: [Injector],
    },
    { provide: PICFLOW_CONFIG, useFactory: (config: AppConfigService) => config, deps: [AppConfigService] },
    { provide: PICFLOW_ANALYTICS, useFactory: (analytics: AnalyticsService) => analytics, deps: [AnalyticsService] },
    {
      provide: PICFLOW_CLERK,
      useFactory: (config: AppConfigService) => {
        return async () => {
          const { Clerk } = await import('@clerk/clerk-js');
          return new Clerk(config.clerkKey);
        };
      },
      deps: [AppConfigService],
    },
    { provide: HANDLE_META, useFactory: (meta: MetaService) => meta.handleMeta.bind(meta), deps: [MetaService] },
    { provide: PICFLOW_STORYBLOK, useFactory: (storyblok: StoryblokService) => storyblok, deps: [StoryblokService] },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(ScrollService),
        inject(AnalyticsService),
        inject(MetaService),
        inject(AppConfigService),
        inject(MaintenanceService),
        inject(LanguageService),
        inject(BrowserService),
        inject(NavigationHistoryService),
        inject(ThemeService),
        inject(Router)
      );
      return initializerFn();
    }),
    provideAppInitializer(() => inject(SentryTracingService).start()),
    provideAppInitializer(() => {
      inject(PageLoadDebugService);
    }),
    provideAppInitializer(startAuthOnTenantSubdomain),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    provideBrowserGlobalErrorListeners(),
    // [{ provide: LOCALE_ID, useValue: 'fr' }],
  ],
};
